import React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowRight,
    Check,
    Loader2,
    Briefcase,
    BookOpen,
    Library,
    CalendarDays,
    Award,
    Layers,
} from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import SEO, { serviceSchema } from "@/components/site/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useLivePricing } from "../lib/useLivePricing";
import { usePageContent } from "../lib/usePageContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUSIONS = [
    { icon: Briefcase, title: "HUMAN+ Snapshot™", body: "Pre-engagement assessment + personalized Executive Report." },
    { icon: Layers, title: "Live Interactive Experience", body: "Guided workshop sessions across all six HUMAN+ pillars." },
    { icon: BookOpen, title: "Digital Workbook", body: "Companion exercises, prompts, and team rituals to apply between sessions." },
    { icon: Library, title: "Resource Library", body: "Curated research, frameworks, and tools you can use immediately." },
    { icon: CalendarDays, title: "30-Day Action Guide", body: "Day-by-day plan to lock the new behaviors into how your team works." },
    { icon: Award, title: "Certificate of Completion", body: "Formal recognition for each participant who completes the experience." },
];

const INDUSTRIES = ["Technology","Healthcare","Higher Education","K-12 Education","Financial Services","Professional Services / Consulting","Manufacturing","Retail / E-commerce","Hospitality","Government / Public Sector","Non-Profit","Media & Communications","Energy & Utilities","Real Estate","Other"];
const ORG_SIZES = ["1 – 10","11 – 50","51 – 200","201 – 500","501 – 1,000","1,001 – 5,000","5,000+"];

const Foundations = () => {
    const { get } = usePageContent("foundations");
    const { byProduct } = useLivePricing();
    const tiers = byProduct("foundations");
    const [selectedTier, setSelectedTier] = React.useState("regular");
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitting },
        reset,
    } = useForm();
    const [done, setDone] = React.useState(false);
    const [paymentState, setPaymentState] = React.useState({
        status: "idle", // idle | polling | paid | cancelled | failed
        amount: null,
        currency: null,
        package_name: null,
    });

    // On mount, detect Stripe return via ?session_id / ?payment
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        const payment = params.get("payment");
        if (payment === "cancelled") {
            setPaymentState((s) => ({ ...s, status: "cancelled" }));
            toast.error("Payment cancelled. You can retry anytime.");
            window.history.replaceState({}, "", window.location.pathname);
            return;
        }
        if (!sessionId) return;
        setPaymentState((s) => ({ ...s, status: "polling" }));
        setDone(true);
        window.scrollTo({ top: 0, behavior: "smooth" });

        let attempts = 0;
        const maxAttempts = 6;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setPaymentState({
                        status: "paid",
                        amount: data.amount,
                        currency: data.currency,
                        package_name: data.package_name,
                    });
                    toast.success("Payment received. You're in.");
                    window.history.replaceState({}, "", window.location.pathname);
                    return;
                }
                if (data.status === "expired") {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                    return;
                }
                if (attempts < maxAttempts) {
                    attempts += 1;
                    setTimeout(poll, 2000);
                } else {
                    setPaymentState((s) => ({ ...s, status: "failed" }));
                }
            } catch (e) {
                setPaymentState((s) => ({ ...s, status: "failed" }));
            }
        };
        poll();
    }, []);

    const industry = watch("industry");
    const organization_size = watch("organization_size");

    const onSubmit = async (data) => {
        try {
            const res = await axios.post(`${API}/foundations/register`, {
                ...data,
                tier_key: selectedTier,
                origin_url: window.location.origin,
            });
            const checkoutUrl = res.data?.checkout?.url;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
                return;
            }
            // Fallback: no checkout returned (e.g. Stripe misconfig) — still confirm.
            setDone(true);
            reset();
            toast.success("Registration received. Check your inbox.");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) {
            toast.error("Could not submit your registration. Please try again.");
        }
    };

    return (
        <div data-testid="foundations-page">
            <SEO
                title="HUMAN+ Foundations Experience™"
                description="The flagship 30-day program that turns the HUMAN+ Framework™ into your team's operating system — guided, applied, and measurable."
                path="/foundations"
                schema={serviceSchema({
                    name: "HUMAN+ Foundations Experience™",
                    description: "Live 30-day executive program across all six HUMAN+ pillars.",
                    price: 795,
                })}
            />
            {/* Header */}
            <section className="bg-indigo_brand text-white">
                <div className="ih-container py-20 md:py-24">
                    <p className="ih-eyebrow" style={{ color: "#08C020" }}>
                        Premier Program
                    </p>
                    <h1 className="mt-3 font-montserrat font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        HUMAN+ Foundations Experience<span className="text-green_brand">™</span>
                    </h1>
                    <p className="mt-7 font-opensans text-white/80 text-lg leading-relaxed max-w-2xl">
                        The flagship program that turns the HUMAN+ Framework™
                        into the operating system of your team — guided,
                        applied, and measurable in 30 days.
                    </p>
                </div>
            </section>

            {/* Overview */}
            <section className="ih-section bg-white">
                <div className="ih-container">
                    <SectionHeading
                        eyebrow="Program Overview"
                        title="From insight to capability — in one focused experience."
                        kicker="Built for executive teams, HR leaders, and organizations adopting AI. Each cohort moves through the six HUMAN+ pillars together, with practical work that compounds across sessions."
                    />

                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {INCLUSIONS.map(({ icon: Icon, title, body }) => (
                            <div key={title} className="ih-card p-7 relative overflow-hidden">
                                <span className="absolute top-0 left-0 h-[3px] w-12 bg-green_brand" aria-hidden="true" />
                                <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-indigo_brand/8">
                                    <Icon size={20} className="text-indigo_brand" />
                                </span>
                                <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-lg">
                                    {title}
                                </h3>
                                <p className="mt-3 font-opensans text-sm text-[#4A5568] leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Tiers */}
            <section className="py-16 sm:py-20 bg-white border-b border-[#EDEFF3]">
                <div className="ih-container">
                    <div className="max-w-2xl mb-10">
                        <p className="ih-eyebrow">{get("pricing.eyebrow", "Investment")}</p>
                        <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl tracking-tight leading-tight">
                            {get("pricing.title", "Choose how your team engages.")}
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {tiers.map((t) => (
                            <button
                                key={t.tier_key}
                                type="button"
                                data-testid={`found-tier-${t.tier_key}`}
                                onClick={() => setSelectedTier(t.tier_key)}
                                className={`text-left ih-card p-6 border-2 transition ${
                                    selectedTier === t.tier_key
                                        ? "border-green_brand shadow-medium"
                                        : "border-transparent hover:border-[#EDEFF3]"
                                }`}
                            >
                                <div className="font-montserrat font-bold text-indigo_brand text-2xl">
                                    ${Number(t.amount_usd).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>
                                <div className="mt-1 font-poppins text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4A5568]">USD</div>
                                <div className="mt-4 font-montserrat font-semibold text-indigo_brand text-base leading-snug">
                                    {t.label}
                                </div>
                                <p className="mt-2 font-opensans text-[#4A5568] text-sm leading-relaxed">
                                    {t.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Registration */}
            <section className="ih-section bg-surface">
                <div className="ih-container grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:items-start">
                    <div className="lg:sticky lg:top-28">
                        <p className="ih-eyebrow">Register Your Organization</p>
                        <h2 className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl tracking-tight leading-tight">
                            Hold a seat in the next cohort.
                        </h2>
                        <p className="mt-5 font-opensans text-[#4A5568] text-base leading-relaxed max-w-md">
                            Submit your details and our team will confirm the
                            next available cohort dates, pricing for your
                            organization size, and onboarding next steps.
                        </p>
                        <ul className="mt-8 space-y-3">
                            {["Six pillars × focused work sessions","One Snapshot + personalized executive report","30-Day Action Guide for sustained change","Certificate of Completion"].map((b) => (
                                <li key={b} className="flex items-start gap-2 font-opensans text-sm text-indigo_brand">
                                    <Check size={16} className="text-green_brand mt-1 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {done ? (
                        <div data-testid="foundations-thanks" className="ih-card p-10 shadow-medium">
                            <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-green_brand/15">
                                <Check size={22} className="text-green_brand" />
                            </span>
                            {paymentState.status === "paid" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl" data-testid="found-paid-headline">
                                        Payment received. You&rsquo;re in.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        We&rsquo;ve received <strong className="text-indigo_brand">
                                        ${paymentState.amount ? paymentState.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""} {paymentState.currency ? paymentState.currency.toUpperCase() : ""}
                                        </strong> for the {paymentState.package_name}. A confirmation receipt is on its way. A member of our team will be in touch within one business day.
                                    </p>
                                </>
                            ) : paymentState.status === "polling" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        Finalizing your payment&hellip;
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        This usually takes a few seconds. Please don&rsquo;t close this window.
                                    </p>
                                </>
                            ) : paymentState.status === "failed" ? (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        We&rsquo;re double-checking your payment.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        If you were charged, you&rsquo;ll receive a confirmation email shortly. Contact us if you need help.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 className="mt-5 font-montserrat font-bold text-indigo_brand text-2xl">
                                        Registration received.
                                    </h3>
                                    <p className="mt-3 font-opensans text-[#4A5568] text-base leading-relaxed">
                                        A confirmation is on its way to your inbox. We&rsquo;ll be in touch within one business day with cohort details.
                                    </p>
                                </>
                            )}
                            <div className="mt-6 flex flex-wrap gap-3">
                                <Link to="/" className="ih-btn-secondary" data-testid="found-return-home">Return Home</Link>
                                <Link to="/discovery" className="ih-btn-ghost">Or schedule a Discovery Call <ArrowRight size={14} /></Link>
                            </div>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="ih-card p-8 md:p-10 shadow-medium grid gap-5 sm:grid-cols-2"
                            data-testid="foundations-form"
                        >
                            <div style={{ display: "none" }} aria-hidden="true">
                                <input
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    {...register("hp_field")}
                                />
                            </div>
                            <Field label="First Name *" testid="found-first_name" error={errors.first_name}>
                                <Input data-testid="found-first_name" {...register("first_name", { required: true })} placeholder="Jordan" className="h-12" />
                            </Field>
                            <Field label="Last Name *" testid="found-last_name" error={errors.last_name}>
                                <Input data-testid="found-last_name" {...register("last_name", { required: true })} placeholder="Reyes" className="h-12" />
                            </Field>
                            <Field label="Work Email *" testid="found-email" error={errors.email} className="sm:col-span-2">
                                <Input data-testid="found-email" type="email" {...register("email", { required: true, pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ })} placeholder="name@organization.com" className="h-12" />
                            </Field>
                            <Field label="Phone" testid="found-phone">
                                <Input data-testid="found-phone" {...register("phone")} placeholder="+1 (555) 000-0000" className="h-12" />
                            </Field>
                            <Field label="Job Title *" testid="found-job_title" error={errors.job_title}>
                                <Input data-testid="found-job_title" {...register("job_title", { required: true })} placeholder="Chief Executive Officer" className="h-12" />
                            </Field>
                            <Field label="Organization Name *" testid="found-organization_name" error={errors.organization_name} className="sm:col-span-2">
                                <Input data-testid="found-organization_name" {...register("organization_name", { required: true })} placeholder="Acme, Inc." className="h-12" />
                            </Field>
                            <Field label="Industry *" testid="found-industry">
                                <Select value={industry} onValueChange={(v) => setValue("industry", v, { shouldValidate: true })}>
                                    <SelectTrigger data-testid="found-industry" className="h-12"><SelectValue placeholder="Select industry" /></SelectTrigger>
                                    <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                                </Select>
                                <input type="hidden" {...register("industry", { required: true })} />
                            </Field>
                            <Field label="Organization Size *" testid="found-organization_size">
                                <Select value={organization_size} onValueChange={(v) => setValue("organization_size", v, { shouldValidate: true })}>
                                    <SelectTrigger data-testid="found-organization_size" className="h-12"><SelectValue placeholder="Select size" /></SelectTrigger>
                                    <SelectContent>{ORG_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                                <input type="hidden" {...register("organization_size", { required: true })} />
                            </Field>
                            <Field label="Anything we should know?" testid="found-notes" className="sm:col-span-2">
                                <Textarea data-testid="found-notes" {...register("notes")} rows={4} placeholder="Cohort timing preferences, team size, particular pillars of focus…" />
                            </Field>

                            <div className="sm:col-span-2 pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <p className="font-opensans text-xs text-[#4A5568]">By submitting, you&rsquo;ll be redirected to secure checkout to complete your registration.</p>
                                <button type="submit" disabled={isSubmitting} data-testid="found-submit" className="ih-btn-primary disabled:opacity-60">
                                    {isSubmitting ? <><Loader2 className="animate-spin" size={16} /> Redirecting to payment…</> : <>Continue to Payment <ArrowRight size={16} /></>}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </section>

            <TestimonialsSection page="foundations" />
        </div>
    );
};

const Field = ({ label, error, children, className = "" }) => (
    <div className={className}>
        <Label className="font-raleway font-semibold text-indigo_brand text-sm">{label}</Label>
        <div className="mt-2">{children}</div>
        {error && <p className="mt-1 font-opensans text-xs text-red-600">This field is required.</p>}
    </div>
);

export default Foundations;
